import { DIFFICULTY_CONFIG } from '../../config/difficulty.config';
import { LEVEL_CONFIG, type JunctionConfig, type SetPiecesConfig } from '../../config/level.config';
import { DifficultyManager } from '../../difficulty/DifficultyManager';
import { DEG_TO_RAD, wrapAngle } from '../../utils/math';
import { Random } from '../../utils/Random';
import { areConnectorsCompatible } from '../connectors/ConnectorMath';
import type { ConnectorDefinition } from '../connectors/ConnectorDefinition';
import { composePose, type Pose } from '../connectors/Pose';
import type { SegmentCatalog } from '../segments/SegmentCatalog';
import { SegmentKind, type SegmentDefinition, type SetPieceWindow, type VehicleRideRules } from '../segments/SegmentDefinition';
import { scheduleMark } from './setPieceSchedule';

export interface GenerationContext {
  /** Track distance where the new segment starts. */
  readonly runDistance: number;
  /** World heading of the exit the new segment attaches to (radians). */
  readonly heading: number;
  /** Committed segments since the last junction. */
  readonly segmentsSinceJunction: number;
  /** Run distance at which a segment id was last placed (for cooldowns). */
  readonly lastPlacedAt: (segmentId: string) => number | undefined;
  /** Committed placements of a segment id in this run (set-piece schedule); unknown = 1 if it was placed, else 0. */
  readonly placements?: (segmentId: string) => number;
  /** Family of the segment the new one attaches to – the same family never follows directly (except a chain run). */
  readonly previousFamily?: string;
  /** The chain run at the end of the committed chain (pendulum cranes, ChainRun) – none: no run going on. */
  readonly chainRun?: ChainRun | null;
  readonly rng: Random;
  /**
   * World pose of the exit the new segment attaches to. Together with `sidewaysAxis` it keeps
   * stretches that head sideways in opposite directions (±90°) apart along the start axis (world z).
   */
  readonly exitPose?: Pose;
  /** Start-axis position (world z) of the latest committed stretch heading sideways to that side (±90°). */
  readonly sidewaysAxis?: (side: -1 | 1) => number | undefined;
  /** Meters of open straight (keepsSightLine) right before the exit – landmark rule; unknown = none. */
  readonly sightLineApproach?: number;
  /** Module the new segment attaches to – a landmark only follows a quiet one. */
  readonly previous?: SegmentDefinition;
  /** Plain straights the run still owes its scheduled set pieces (PlainDebt) – unknown: none. */
  readonly plainDebt?: PlainDebt;
}

/**
 * A run of a module with a chain rule (SegmentGenerationRules.chain): its id, the modules placed so far and the length
 * drawn when it started. A landmark right behind a module of an unfinished run pauses it (the run goes on behind the ad
 * gantry); any other module ends it.
 */
export interface ChainRun {
  readonly id: string;
  readonly count: number;
  readonly length: number;
}

/** Tag of modules that only come inside a ride (SegmentGenerationRules.ride) – the ructec-Bulli's road and ramps. */
export const RIDE_ONLY_TAG = 'ride-only';

/** Own random stream for the lengths of chain runs (SegmentGenerationRules.chain). */
const CHAIN_STREAM = 0x51ed27;

/** Longest run a chain rule can draw at any distance (all stages) – 1 without a chain rule. */
export function chainLongest(definition: SegmentDefinition): number {
  const chain = definition.generation.chain;
  if (!chain) return 1;
  return Math.max(1, ...[chain.lengths, ...(chain.from ?? []).map((stage) => stage.lengths)].flat().map(([count]) => count));
}

/** Length of a run of `definition` starting at run distance `start` – its own stream from the run seed and the start. */
export function chainLength(definition: SegmentDefinition, seed: number, start: number): number {
  const chain = definition.generation.chain;
  if (!chain) return 1;
  const lengths = (chain.from ?? []).filter((stage) => start >= stage.distance).at(-1)?.lengths ?? chain.lengths;
  return new Random(Random.deriveSeed(Random.deriveSeed(seed, CHAIN_STREAM), Math.round(start))).weighted(lengths) ?? 1;
}

/**
 * The chain run after `definition` was placed behind `previous` at run distance `at` (TrackManager keeps it; `seed` is
 * the seed of the track stream). A module of the same run continues it only while the run is not complete – otherwise
 * (e.g. a crane picked normally behind a gantry after a complete run) a new run starts.
 */
export function chainRunAfter(run: ChainRun | null, previous: SegmentDefinition, definition: SegmentDefinition, at: number, seed: number): ChainRun | null {
  if (definition.generation.chain) {
    const behindRun = run?.id === definition.id && (previous.id === definition.id || previous.generation.landmark !== undefined);
    if (run && behindRun && run.count < run.length) return { ...run, count: run.count + 1 };
    return { id: definition.id, count: 1, length: chainLength(definition, seed, at) };
  }
  return run && definition.generation.landmark && previous.id === run.id && run.count < run.length ? run : null;
}

/**
 * Scheduled set pieces take their track from the plain straights: every placed set piece owes its length in plain
 * straights. A plain straight picked while meters are owed is paid off (its length off the debt) and the pick goes
 * to another module – so junctions, curves, enclosures, moving hazards, terrain modules and landmarks keep their
 * frequency per km (the TrackManager keeps the debt of the run).
 */
export interface PlainDebt {
  readonly meters: number;
  pay(meters: number): void;
}

/**
 * Open straight (landmark sight line, see LandmarkRules): no junction or curve (the heading stays),
 * no enclosure or overhead structure (scaffold passage, pipe bridge) that would hide a landmark ahead.
 */
export function keepsSightLine(definition: SegmentDefinition): boolean {
  return (
    definition.kind === SegmentKind.Straight &&
    !definition.junction &&
    !definition.curve &&
    !definition.enclosure &&
    (definition.terrain?.ceilings.length ?? 0) === 0
  );
}

/** Calm module (direct neighbours of a landmark): open straight without moving hazard or scripted event. */
export function isCalmModule(definition: SegmentDefinition): boolean {
  return keepsSightLine(definition) && !definition.collapseEvent && (definition.hazards?.length ?? 0) === 0;
}

/**
 * Quiet module (direct predecessor of a landmark): no junction exit, no scripted event, no roof or
 * overhead passage – under a ceiling the runner would see the high banner only at the exit. Curves and
 * moving hazards are fine: the landmark's own module approach is straight, its banner hangs above
 * vehicles and loads, and the variety of the track stays (user 2026-10-02).
 */
export function isQuietModule(definition: SegmentDefinition): boolean {
  return !definition.junction && !definition.enclosure && !definition.collapseEvent && (definition.terrain?.ceilings.length ?? 0) === 0;
}

/** Plain straight (calm, flat, no landmark) – a landmark takes the pick share of these. */
export function isPlainStraight(definition: SegmentDefinition): boolean {
  return isCalmModule(definition) && !definition.terrain && !definition.generation.landmark;
}

/** Open straight before the next exit after `definition` was appended to a stretch of `before` meters. */
export function sightLineAfter(before: number, definition: SegmentDefinition): number {
  return keepsSightLine(definition) ? before + definition.length : 0;
}

/** Summed pick weight of the plain straights among the candidates – the share a landmark takes. */
function plainShareOf(candidates: readonly (readonly [SegmentDefinition, number])[]): number {
  return candidates.filter(([definition]) => isPlainStraight(definition)).reduce((sum, [, weight]) => sum + weight, 0);
}

/** Exits closer than this to ±90° count as sideways (radians). */
const SIDEWAYS_TOLERANCE = 1e-3;

/** The hazard-module weight factor of the difficulty tier at a run distance. */
export function tierHazardScale(difficulty: Pick<DifficultyManager, 'tierAt'>): (runDistance: number) => number {
  return (runDistance) => difficulty.tierAt(runDistance).hazardWeightScale;
}

/** The special-module weight factor of the difficulty tier at a run distance. */
export function tierSpecialScale(difficulty: Pick<DifficultyManager, 'tierAt'>): (runDistance: number) => number {
  return (runDistance) => difficulty.tierAt(runDistance).specialWeightScale;
}

/** The plain-straight weight factor of the difficulty tier at a run distance. */
export function tierPlainScale(difficulty: Pick<DifficultyManager, 'tierAt'>): (runDistance: number) => number {
  return (runDistance) => difficulty.tierAt(runDistance).plainWeightScale;
}


/** −1 / +1 if the world heading points sideways (±90° from the start direction), else 0. */
export function sidewaysSide(heading: number): -1 | 0 | 1 {
  const h = wrapAngle(heading);
  return Math.abs(Math.abs(h) - Math.PI / 2) < SIDEWAYS_TOLERANCE ? (h < 0 ? -1 : 1) : 0;
}

/**
 * Chooses the next track module. Candidates must be connector-compatible,
 * unlocked at the current distance and – for junctions – respect spacing and
 * heading rules: no exit may lead further than ±maxHeading from the start
 * direction. That keeps the track monotone along the start axis, so it can
 * never run back into itself. Junctions and curves only lead sideways (±90°) once
 * the track is `minReversalSeparation` past the last stretch that ran sideways the
 * other way, so the two pass each other with their side dressing clear. For
 * variety, a segment family (e.g. all small pits) never follows itself directly – except a module with a chain
 * rule (pendulum crane): it comes in runs of a random length (chainedModule).
 * Landmarks (ad gantry) stand in the middle of long calm straights (LandmarkRules). Scheduled set
 * pieces (golden bottle ramp, dozer) are never picked at random: they are placed once at each mark of their
 * schedule behind a reserved open straight (SetPieceSchedule) – and never crowd each other (setPieceBlocked).
 */
export class LevelGenerator {
  private readonly landmarks: readonly SegmentDefinition[];
  private readonly setPieces: readonly SegmentDefinition[];

  constructor(
    private readonly catalog: SegmentCatalog,
    private readonly junctions: JunctionConfig,
    private readonly setPieceRules: SetPiecesConfig = LEVEL_CONFIG.setPieces,
    /** Pick-weight factor of modules with moving hazards at a run distance (DifficultyTier.hazardWeightScale). */
    private readonly hazardScaleAt: (runDistance: number) => number = tierHazardScale(new DifficultyManager(DIFFICULTY_CONFIG)),
    /** Pick-weight factor of the plain straights at a run distance (DifficultyTier.plainWeightScale). */
    private readonly plainScaleAt: (runDistance: number) => number = tierPlainScale(new DifficultyManager(DIFFICULTY_CONFIG)),
    /** Pick-weight factor of the special modules without moving hazards (DifficultyTier.specialWeightScale). */
    private readonly specialScaleAt: (runDistance: number) => number = tierSpecialScale(new DifficultyManager(DIFFICULTY_CONFIG)),
  ) {
    this.landmarks = catalog.all().filter((definition) => definition.generation.landmark);
    this.setPieces = catalog.all().filter((definition) => definition.generation.schedule);
  }

  /**
   * Pick weight at the run distance: modules with moving hazards come more often in the later difficulty tiers, the
   * plain straights less often.
   */
  private weightAt(definition: SegmentDefinition, runDistance: number): number {
    const weight = definition.generation.weight;
    const moving = (definition.hazards?.length ?? 0) > 0 && !definition.generation.schedule && !definition.generation.landmark;
    if (moving) return weight * this.hazardScaleAt(runDistance);
    // the ructec-Bulli's own road, junctions, landmarks and set pieces keep their weight
    const g = definition.generation;
    if (g.tags.includes(RIDE_ONLY_TAG) || definition.junction || g.landmark || g.schedule) return weight;
    // plain straights give way, the special modules (pits, scaffolds, containers, tunnels, curves …) come more often
    return weight * (isPlainStraight(definition) ? this.plainScaleAt(runDistance) : this.specialScaleAt(runDistance));
  }

  pickNext(exit: ConnectorDefinition, ctx: GenerationContext): SegmentDefinition {
    // A pair: the module before demands its partner (the container tower whose column leads onto the shell's upper floor).
    const partnerId = ctx.previous?.generation.followedBy;
    const partner = partnerId ? this.catalog.all().find((definition) => definition.id === partnerId) : undefined;
    if (partner && areConnectorsCompatible(exit, partner.entry)) return partner;
    const maxHeading = this.junctions.maxHeadingDegrees * DEG_TO_RAD + 1e-6;
    const chain = this.chainedModule(ctx);
    const fitting = this.catalog
      .all()
      .filter((definition) => areConnectorsCompatible(exit, definition.entry))
      .filter((definition) => definition.generation.minDistance <= ctx.runDistance && ctx.runDistance < (definition.generation.maxDistance ?? Number.POSITIVE_INFINITY))
      .filter((definition) => !definition.junction || ctx.segmentsSinceJunction >= this.junctions.minSegmentsBetween)
      .filter((definition) => ctx.segmentsSinceJunction >= (definition.generation.minSegmentsAfterJunction ?? 0))
      .filter((definition) => {
        // A chain run ignores its own cooldown (the collapse comes twice in a row) – it counts from the run's last module.
        if (definition === chain) return true;
        const last = ctx.lastPlacedAt(definition.id);
        const cooldown = definition.generation.cooldownDistance ?? 0;
        return last === undefined || ctx.runDistance - last >= cooldown;
      })
      .filter((definition) => {
        const own = definition.generation.maxExitHeadingDegrees;
        const limit = own === undefined ? maxHeading : Math.min(maxHeading, own * DEG_TO_RAD + 1e-6);
        return definition.exits.every((e) => Math.abs(wrapAngle(ctx.heading + e.transform.yaw)) <= limit);
      })
      .filter((definition) => definition.exits.every((e) => this.clearOfOppositeRun(e, ctx)))
      .filter((definition) => this.fitsLandmarkRules(definition, ctx))
      .filter((definition) => this.fitsSchedule(definition, ctx))
      .filter((definition) => this.fitsRide(definition, ctx))
      .map((definition) => [definition, this.weightAt(definition, ctx.runDistance)] as const);

    // Variety: never the same segment family twice in a row (e.g. two pits or two scaffold passages).
    const candidates = fitting.filter(([definition]) => definition.generation.family === undefined || definition.generation.family !== ctx.previousFamily);
    // Inside a ride a road module always fits – the variety rules never leave the van without road.
    const ride = this.activeRide(ctx);
    if (ride && candidates.length === 0) {
      const any = this.choose(fitting, ctx); // e.g. the finale right behind a van ramp of the same family
      if (any) return any;
      const road = this.catalog
        .all()
        .find((d) => d.generation.tags.includes(ride.roadTag) && !d.generation.tags.includes(ride.finale?.tag ?? '\0') && d.kind === SegmentKind.Straight && areConnectorsCompatible(exit, d.entry));
      if (road) return road;
    }
    // …except a chain run that is not complete yet (pendulum cranes, collapse): it goes on.
    if (chain && fitting.some(([definition]) => definition === chain)) {
      const next = this.continueChain(chain, candidates, ctx);
      if (next) return next;
    }

    const picked = this.choose(candidates, ctx);
    if (!picked) throw new Error(`No segment fits exit ${exit.id} (${exit.allowedNextProfiles.join(', ')})`);
    return picked;
  }

  /**
   * The module of the chain run going on, if the run is not complete yet (its length was drawn when it started,
   * chainRunAfter) – right behind a module of the run or behind a landmark that paused it.
   */
  private chainedModule(ctx: GenerationContext): SegmentDefinition | undefined {
    const run = ctx.chainRun;
    const previous = ctx.previous;
    if (!run || run.count >= run.length || !previous || (previous.id !== run.id && !previous.generation.landmark)) return undefined;
    const module = this.catalog.all().find((definition) => definition.id === run.id);
    return module?.generation.chain ? module : undefined;
  }

  /**
   * Next module of a chain run – or undefined: the run ends and the usual pick follows. A scheduled set piece waits
   * for the run as long as its window allows (it starts by `latestStart` – the run ends a module before, if needed);
   * an overdue landmark ends the run. A landmark that fits keeps its usual chance (the share of the plain straights,
   * chooseModule): it stands inside the run, which goes on behind it – the ad gantries keep their frequency per km.
   */
  private continueChain(
    chain: SegmentDefinition,
    candidates: readonly (readonly [SegmentDefinition, number])[],
    ctx: GenerationContext,
  ): SegmentDefinition | undefined {
    for (const [definition] of candidates) {
      if (definition.generation.schedule && this.setPieceWindowCloses(definition, chain, ctx)) return undefined;
      if (definition.generation.landmark && this.landmarkOverdue(definition, ctx)) return undefined;
    }
    const landmark = candidates.find(([definition]) => definition.generation.landmark)?.[0];
    if (!landmark || ctx.previous?.id !== chain.id) return chain;
    const plainShare = this.landmarkShare(candidates, ctx);
    if (plainShare <= 0) return chain;
    const total = candidates.reduce((sum, [, weight]) => sum + weight, 0);
    return ctx.rng.weighted([
      [chain, Math.max(0, total - plainShare)],
      [landmark, plainShare],
    ] as const);
  }

  /** Behind one more module of the run the set piece (it fits now) would start past the end of its window. */
  private setPieceWindowCloses(setPiece: SegmentDefinition, chain: SegmentDefinition, ctx: GenerationContext): boolean {
    const window = this.pendingWindow(setPiece, ctx);
    return window !== undefined && ctx.runDistance + chain.length > window.latestStart;
  }

  /**
   * Weighted pick. A landmark that fits takes the share of the plain straights (it is one), so
   * junctions, curves, enclosures, hazards and set pieces keep their frequency; once it is overdue
   * (first one at minDistance, then `targetSpacing` if set) it takes its spot right away. A due
   * scheduled set piece goes first (chooseSetPiece). A plain straight picked while set pieces are owed
   * (PlainDebt) is paid off – the pick goes to another module.
   */
  private choose(candidates: readonly (readonly [SegmentDefinition, number])[], ctx: GenerationContext): SegmentDefinition | undefined {
    const setPiece = candidates.find(([definition]) => definition.generation.schedule)?.[0];
    if (setPiece) return this.chooseSetPiece(setPiece, candidates, ctx);
    const picked = this.chooseModule(candidates, ctx);
    const debt = ctx.plainDebt;
    if (!picked || !debt || debt.meters <= 0 || !isPlainStraight(picked)) return picked;
    const others = candidates.filter(([definition]) => !isCalmModule(definition) || definition.generation.landmark);
    if (!others.some(([definition, weight]) => weight > 0 && !definition.generation.landmark)) return picked;
    debt.pay(picked.length);
    return this.chooseModule(others, ctx, this.landmarkShare(candidates, ctx)) ?? picked;
  }

  /**
   * The share a fitting landmark takes: the plain straights' weight as it would be WITHOUT the tier's plainWeightScale –
   * fewer plain straights later must not mean fewer ad gantries (their banners are sold).
   */
  private landmarkShare(candidates: readonly (readonly [SegmentDefinition, number])[], ctx: GenerationContext): number {
    return plainShareOf(candidates) / Math.max(1e-6, this.plainScaleAt(ctx.runDistance));
  }

  private chooseModule(
    candidates: readonly (readonly [SegmentDefinition, number])[],
    ctx: GenerationContext,
    plainShare = this.landmarkShare(candidates, ctx),
  ): SegmentDefinition | undefined {
    const landmark = candidates.find(([definition]) => definition.generation.landmark)?.[0];
    if (!landmark) return ctx.rng.weighted(candidates);
    if (this.landmarkOverdue(landmark, ctx)) return landmark;
    const others = candidates.filter(([definition]) => definition !== landmark && !isPlainStraight(definition));
    return ctx.rng.weighted([...others, [landmark, plainShare] as const]);
  }

  /**
   * A landmark needs `sightLine` meters of open straight right before it and a quiet module as
   * direct predecessor (LandmarkRules). Once it is due, the generator reserves that open straight
   * (with sightLine 0 nothing has to be reserved). Behind a landmark, `calmAfter` meters take calm
   * modules only.
   */
  private fitsLandmarkRules(definition: SegmentDefinition, ctx: GenerationContext): boolean {
    const own = definition.generation.landmark;
    if (own) return (ctx.sightLineApproach ?? 0) >= own.sightLine && ctx.previous !== undefined && isQuietModule(ctx.previous);
    if (this.insideLandmarkCalm(ctx)) return isCalmModule(definition);
    const due = this.dueLandmark(ctx);
    if (!due) return true;
    // only the sight line is reserved – the quiet predecessor comes by itself within a pick or two
    return (due.generation.landmark?.sightLine ?? 0) === 0 || keepsSightLine(definition);
  }

  /** The landmark that has to stand within one sight line from here (minDistance / targetSpacing reached). */
  private dueLandmark(ctx: GenerationContext): SegmentDefinition | undefined {
    return this.landmarks.find((landmark) => ctx.runDistance + (landmark.generation.landmark?.sightLine ?? 0) >= this.landmarkTarget(landmark, ctx));
  }

  private landmarkOverdue(landmark: SegmentDefinition, ctx: GenerationContext): boolean {
    return ctx.runDistance >= this.landmarkTarget(landmark, ctx);
  }

  /** Run distance by which the landmark should stand: the first at its minDistance, then `targetSpacing` after the last one (if set). */
  private landmarkTarget(landmark: SegmentDefinition, ctx: GenerationContext): number {
    const rules = landmark.generation.landmark;
    if (!rules) return Infinity;
    const last = ctx.lastPlacedAt(landmark.id);
    if (last === undefined) return landmark.generation.minDistance;
    return rules.targetSpacing === undefined ? Infinity : last + rules.targetSpacing;
  }

  /**
   * Scheduled set pieces (SetPieceSchedule): the set piece itself fits only from its pending mark on (the
   * first mark not served yet in this run), behind a quiet module and with `sightLine` meters of open
   * straight before it – past the window end (`latestStart`) the quiet module suffices. From the mark on,
   * until the set piece stands, only modules that keep the sight line follow (reservesSightLine); before
   * the mark the track mix is untouched. A schedule with an end: after its last mark, never again.
   */
  private fitsSchedule(definition: SegmentDefinition, ctx: GenerationContext): boolean {
    const own = definition.generation.schedule;
    if (own) {
      const window = this.pendingWindow(definition, ctx);
      if (!window || ctx.runDistance < window.from || this.setPieceBlocked(definition, ctx)) return false;
      const approach = (ctx.sightLineApproach ?? 0) >= own.sightLine || ctx.runDistance >= window.latestStart;
      return approach && ctx.previous !== undefined && isQuietModule(ctx.previous);
    }
    // a module of a due set piece's family would keep it out for one more pick (never the same family twice) – not now
    if (this.setPieces.some((setPiece) => this.blocksDueSetPiece(definition, setPiece, ctx))) return false;
    return !this.setPieces.some((setPiece) => this.reservesSightLine(setPiece, ctx)) || keepsSightLine(definition);
  }

  /** definition shares the family of setPiece, whose window is open right now (from its mark on). */
  private blocksDueSetPiece(definition: SegmentDefinition, setPiece: SegmentDefinition, ctx: GenerationContext): boolean {
    const family = setPiece.generation.family;
    if (!family || definition.generation.family !== family) return false;
    const window = this.pendingWindow(setPiece, ctx);
    return window !== undefined && ctx.runDistance >= window.from;
  }

  /**
   * The window of the first mark not served yet in this run (run distances), or undefined. The whole
   * schedule moves with minDistance (testing aid ?segment=: minDistance 40 → the first mark at 40 m).
   */
  private pendingWindow(setPiece: SegmentDefinition, ctx: GenerationContext): SetPieceWindow | undefined {
    const schedule = setPiece.generation.schedule;
    if (!schedule) return undefined;
    const placed = ctx.placements?.(setPiece.id) ?? (ctx.lastPlacedAt(setPiece.id) === undefined ? 0 : 1);
    const shift = setPiece.generation.minDistance - (scheduleMark(schedule, 0) ?? setPiece.generation.minDistance);
    // Marks whose window a ride covers (ructec-Bulli) are left out – the set piece goes on with its next mark after it
    // instead of three golden ramps in a row behind the van.
    let index = placed + this.skippedMarks(setPiece, ctx);
    let mark = scheduleMark(schedule, index, shift);
    while (mark !== undefined && this.insideRide(setPiece, mark, mark + schedule.window, ctx)) mark = scheduleMark(schedule, ++index, shift);
    return mark === undefined ? undefined : { from: mark, latestStart: mark + schedule.window };
  }

  /** Marks of `setPiece` before its pending one that a ride covered (left out, see pendingWindow). */
  private skippedMarks(setPiece: SegmentDefinition, ctx: GenerationContext): number {
    const schedule = setPiece.generation.schedule;
    if (!schedule || setPiece.generation.ride) return 0;
    const placed = ctx.placements?.(setPiece.id) ?? (ctx.lastPlacedAt(setPiece.id) === undefined ? 0 : 1);
    const shift = setPiece.generation.minDistance - (scheduleMark(schedule, 0) ?? setPiece.generation.minDistance);
    let skipped = 0;
    let served = 0;
    for (let index = 0; served < placed; index++) {
      const mark = scheduleMark(schedule, index, shift);
      if (mark === undefined) break;
      if (this.insideRide(setPiece, mark, mark + schedule.window, ctx)) skipped++;
      else served++;
    }
    return skipped;
  }

  /** The stretch [from, to) overlaps the ride of another set piece (its module plus its ride). */
  private insideRide(setPiece: SegmentDefinition, from: number, to: number, ctx: GenerationContext): boolean {
    for (const other of this.setPieces) {
      const ride = other.generation.ride;
      if (!ride || other === setPiece) continue;
      const placedAt = ctx.lastPlacedAt(other.id);
      if (placedAt === undefined) continue;
      if (from < placedAt + other.length + ride.length && to > placedAt) return true;
    }
    return false;
  }

  /**
   * The set piece is due (its pending mark is reached) and still waits for its open straight: only modules
   * that keep the sight line follow. Reserving only from the mark on (not before it) keeps the share of
   * junctions, curves and enclosures – with marks every 500 m a reservation before each one cost too many.
   */
  private reservesSightLine(setPiece: SegmentDefinition, ctx: GenerationContext): boolean {
    // With sightLine 0 nothing has to be reserved (like a landmark); a set piece that gives way to another one
    // reserves nothing while it waits – the track mix stays.
    if ((setPiece.generation.schedule?.sightLine ?? 0) <= 0) return false;
    const window = this.pendingWindow(setPiece, ctx);
    return window !== undefined && ctx.runDistance >= window.from && ctx.runDistance < window.latestStart && !this.setPieceBlocked(setPiece, ctx);
  }

  /**
   * Scheduled set pieces never crowd each other (SetPiecesConfig.minGap): one starts at least `minGap` m after the
   * end of the last one placed, and one with a lower priority does not start where it would end within `minGap` of
   * the pending mark of one with a higher priority – it waits until that one stands. So the golden ramp keeps its
   * marks exactly, and the dozer (≈ every 600 m) gives way where its marks meet them – later, never skipped. A set
   * piece that gives way can be pushed up to its own next mark: it keeps the gap to its own last placement too.
   */
  private setPieceBlocked(setPiece: SegmentDefinition, ctx: GenerationContext): boolean {
    const gap = this.setPieceRules.minGap;
    const priority = setPiece.generation.schedule?.priority ?? 0;
    const yields = this.yieldsToSetPiece(setPiece);
    for (const other of this.setPieces) {
      if (other === setPiece && !yields) continue; // never pushed back: its marks lie a schedule spacing apart
      const last = ctx.lastPlacedAt(other.id);
      // a ride set piece (ructec-Bulli) occupies its whole ride, not only its module
      if (last !== undefined && ctx.runDistance < last + other.length + (other.generation.ride?.length ?? 0) + gap) return true;
      if ((other.generation.schedule?.priority ?? 0) <= priority) continue;
      const window = this.pendingWindow(other, ctx);
      if (window && window.from < ctx.runDistance + setPiece.length + gap) return true;
    }
    return false;
  }

  /** Another scheduled set piece has a higher priority – this one gives way where their marks meet. */
  private yieldsToSetPiece(setPiece: SegmentDefinition): boolean {
    const priority = setPiece.generation.schedule?.priority ?? 0;
    return this.setPieces.some((other) => (other.generation.schedule?.priority ?? 0) > priority);
  }

  /**
   * A due set piece that fits (fitsSchedule) first takes the share of the calm modules – as the ad gantry
   * takes that of the plain straights – so moving hazards and scripted events keep their frequency; from
   * `forceAfter` meters past its mark on it takes the spot at once.
   */
  private chooseSetPiece(
    setPiece: SegmentDefinition,
    candidates: readonly (readonly [SegmentDefinition, number])[],
    ctx: GenerationContext,
  ): SegmentDefinition {
    const schedule = setPiece.generation.schedule;
    // A set piece that gives way (to a set piece of higher priority) lets a landmark that fits go first and follows
    // right behind it – inside the landmark's minimum spacing, where no other landmark may stand: the landmark
    // rhythm stays (the dozer every ≈ 600 m would stretch the gantry gaps otherwise).
    const landmark = this.yieldsToSetPiece(setPiece) ? candidates.find(([definition]) => definition.generation.landmark)?.[0] : undefined;
    const window = this.pendingWindow(setPiece, ctx);
    if (!schedule || !window || ctx.runDistance >= window.from + schedule.forceAfter) return setPiece;
    if (landmark) return this.chooseModule(candidates.filter(([definition]) => definition !== setPiece), ctx) ?? setPiece;
    // a landmark waits for the next pick – the set piece goes first
    const rest = candidates.filter(([definition]) => definition !== setPiece && !definition.generation.landmark);
    // the calm modules' share as it would be without the tier's plainWeightScale (fewer plain straights later must not
    // delay the set pieces)
    const plainScale = Math.max(1e-6, this.plainScaleAt(ctx.runDistance));
    const calmShare = rest
      .filter(([definition]) => isCalmModule(definition))
      .reduce((sum, [definition, weight]) => sum + (isPlainStraight(definition) ? weight / plainScale : weight), 0);
    return ctx.rng.weighted([...rest.filter(([definition]) => !isCalmModule(definition)), [setPiece, calmShare] as const]) ?? setPiece;
  }

  /**
   * Inside a ride (ructec-Bulli, SegmentGenerationRules.ride) only road modules follow: straights the van can pass,
   * curves, crossings and the ad gantry (tagged `roadTag`) – no hazards, terrain climbs, enclosures or forced turns.
   */
  private fitsRide(definition: SegmentDefinition, ctx: GenerationContext): boolean {
    const stretch = this.rideStretch(ctx);
    const tags = definition.generation.tags;
    // modules made for the ride (tag RIDE_ONLY_TAG: van slalom, van ramp) never come on foot
    if (!stretch) return !tags.includes(RIDE_ONLY_TAG);
    if (!tags.includes(stretch.ride.roadTag)) return false;
    const finale = stretch.ride.finale;
    if (!finale) return true;
    // the finale (big ramp): only near the end, once – and there it comes first
    const due = stretch.to - ctx.runDistance <= finale.before && !this.finalePlaced(finale.tag, stretch.from, ctx);
    return tags.includes(finale.tag) ? due : !due;
  }

  /** A module tagged `tag` was placed since `from` (run distance). */
  private finalePlaced(tag: string, from: number, ctx: GenerationContext): boolean {
    return this.catalog.all().some((d) => {
      if (!d.generation.tags.includes(tag)) return false;
      const at = ctx.lastPlacedAt(d.id);
      return at !== undefined && at >= from - 1e-6;
    });
  }

  /** The ride rules of the ride set piece whose stretch the new segment starts in, or undefined. */
  private activeRide(ctx: GenerationContext): VehicleRideRules | undefined {
    return this.rideStretch(ctx)?.ride;
  }

  /** The ride stretch (run distances) the new segment starts in, with its rules, or undefined. */
  private rideStretch(ctx: GenerationContext): { ride: VehicleRideRules; from: number; to: number } | undefined {
    for (const setPiece of this.setPieces) {
      const ride = setPiece.generation.ride;
      const placedAt = ride ? ctx.lastPlacedAt(setPiece.id) : undefined;
      if (!ride || placedAt === undefined) continue;
      const from = placedAt + setPiece.length;
      if (ctx.runDistance >= from - 1e-6 && ctx.runDistance < from + ride.length) return { ride, from, to: from + ride.length };
    }
    return undefined;
  }

  /** The new segment would start inside the calm stretch behind a landmark. */
  private insideLandmarkCalm(ctx: GenerationContext): boolean {
    return this.landmarks.some((landmark) => {
      const placedAt = ctx.lastPlacedAt(landmark.id);
      const calmAfter = landmark.generation.landmark?.calmAfter ?? 0;
      return placedAt !== undefined && ctx.runDistance >= placedAt && ctx.runDistance < placedAt + landmark.length + calmAfter;
    });
  }

  /** A sideways exit must lie far enough along the start axis past the last opposite sideways stretch. */
  private clearOfOppositeRun(exit: ConnectorDefinition, ctx: GenerationContext): boolean {
    if (!ctx.exitPose || !ctx.sidewaysAxis) return true;
    const side = sidewaysSide(ctx.heading + exit.transform.yaw);
    if (side === 0) return true;
    const opposite = ctx.sidewaysAxis(side === 1 ? -1 : 1);
    if (opposite === undefined) return true;
    return composePose(ctx.exitPose, exit.transform).z - opposite >= this.junctions.minReversalSeparation;
  }
}
